import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import Header from "../../../components/ui/Header";
import Sidebar from "../../../components/ui/Sidebar";
import Breadcrumb from "../../../components/ui/Breadcrumb";
import Button from "../../../components/ui/Button";
import Icon from "../../../components/AppIcon";
import axiosInstance from "../../../utils/axiosInstance";

dayjs.extend(utc);
dayjs.extend(timezone);

const IST_TZ = "Asia/Kolkata";

const ExpiringOrders = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed] = useState(false);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);

  const navigate = useNavigate();

  const ITEMS_PER_PAGE = 10;

  const fetchExpiringOrders = async (page = 1) => {
    try {
      setLoading(true);

      const res = await axiosInstance.get(
        `${import.meta.env.VITE_API_BASE_URL}/api/orders/expiring`,
        {
          params: { page, limit: ITEMS_PER_PAGE },
        }
      );

      setOrders(res.data.orders || []);
      setTotalOrders(res.data.total || 0);
      setTotalPages(res.data.totalPages || 1);
      setCurrentPage(page);
    } catch (err) {
      console.error("Failed to fetch expiring orders", err);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Fix interval: always use latest currentPage
  useEffect(() => {
    fetchExpiringOrders(1);

   

  
  }, []);

  const getSerialNumber = (index) => (currentPage - 1) * ITEMS_PER_PAGE + index + 1;

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) fetchExpiringOrders(page);
  };

  /* -----------------------------
    IST helpers
  ----------------------------- */

  // order.date is like 2026-01-30T03:30:00.000Z (represents IST day)
  const getOrderDayIST = (orderDate) => dayjs(orderDate).tz(IST_TZ).startOf("day");

  // slot.startTime is 1970-01-01T15:01:00.000Z -> convert to IST time-of-day
  const getSlotStartEndIST = (order) => {
    const slot = order?.slot;
    if (!slot?.startTime || !slot?.endTime || !order?.date) return null;

    const orderDayIST = getOrderDayIST(order.date);

    const startTOD = dayjs(slot.startTime).tz(IST_TZ);
    const endTOD = dayjs(slot.endTime).tz(IST_TZ);

    const startIST = orderDayIST
      .hour(startTOD.hour())
      .minute(startTOD.minute())
      .second(0)
      .millisecond(0);

    const endIST = orderDayIST
      .hour(endTOD.hour())
      .minute(endTOD.minute())
      .second(0)
      .millisecond(0);

    return { startIST, endIST };
  };

  const formatISTDate = (date) => {
    if (!date) return "N/A";
    return dayjs(date).tz(IST_TZ).format("DD MMM YYYY");
  };

  const formatISTDateTime = (date) => {
    if (!date) return "N/A";
    return dayjs(date).tz(IST_TZ).format("DD MMM YYYY, hh:mm A");
  };

  const formatSlotIST = (order) => {
    const win = getSlotStartEndIST(order);
    if (!win) return "N/A";
    return `${win.startIST.format("hh:mm A")} - ${win.endIST.format("hh:mm A")} IST`;
  };

  const getMinsLeftToSlotStart = (order) => {
    const win = getSlotStartEndIST(order);
    if (!win) return null;

    const nowIST = dayjs().tz(IST_TZ);
    return win.startIST.diff(nowIST, "minute");
  };

  const formatAddress = (addr) => {
    if (!addr) return "N/A";
    const parts = [
      addr.saveas ? `${addr.saveas}` : null,
      addr.address || null,
      addr.landmark ? `Landmark: ${addr.landmark}` : null,
      `${addr.city || ""}${addr.city && addr.state ? ", " : ""}${addr.state || ""}`.trim() || null,
      addr.pincode ? `PIN: ${addr.pincode}` : null,
    ].filter(Boolean);

    return parts.join(" • ");
  };

  const formatDurationDHMS = (totalMinutes) => {
  const absMins = Math.abs(totalMinutes);

  const days = Math.floor(absMins / (60 * 24));
  const hours = Math.floor((absMins % (60 * 24)) / 60);
  const mins = absMins % 60;

  const parts = [];
  if (days) parts.push(`${days}d`);
  if (hours || days) parts.push(`${hours}h`);
  parts.push(`${mins}m`); // always show mins

  return parts.join(" ");
};

const getTimeLeftText = (order) => {
  const win = getSlotStartEndIST(order); // from your existing helper
  if (!win) return "N/A";

  const nowIST = dayjs().tz(IST_TZ);
  const diffMins = win.startIST.diff(nowIST, "minute"); // +ve => future, -ve => overdue

  if (diffMins > 0) return `In ${formatDurationDHMS(diffMins)}`;
  if (diffMins === 0) return "Starting now";
  return ` ${formatDurationDHMS(diffMins)}`;
};

  return (
    <div className="min-h-screen bg-gray-50">
      <Header onMenuToggle={() => setSidebarOpen(!sidebarOpen)} />
      <Sidebar isOpen={sidebarOpen} />

      <main
        className={`pt-16 transition-all duration-300 ${
          sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
        }`}
      >
        <div className="p-4">
          <Breadcrumb />

          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Icon name="Clock" size={20} className="text-red-600" />
                Expiring Orders
              </h1>
              <p className="text-xs text-gray-600">
                Orders that are within 30 minutes of slot start or already overdue
              </p>
            </div>

            <div className="flex gap-2">
              {/* ✅ Back button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(-1)}
                className="text-xs"
              >
                <Icon name="ArrowLeft" size={14} className="mr-1" />
                Back
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchExpiringOrders(currentPage)}
                className="text-xs"
              >
                <Icon name="RefreshCw" size={14} className="mr-1" />
                Refresh
              </Button>
            </div>
          </div>

          {/* Stats */}
          <div className="mb-4 p-3 bg-white rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                <div>
                  <p className="text-sm text-gray-600">Total Orders</p>
                  <p className="text-lg font-semibold text-gray-900">{totalOrders}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Current Page</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {currentPage} / {totalPages}
                  </p>
                </div>
              </div>

              <div className="text-xs text-gray-500">
                Now (IST): {dayjs().tz(IST_TZ).format("DD MMM YYYY, hh:mm A")}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 bg-red-50 border-b border-red-200">
              <h3 className="text-sm font-semibold text-red-700">
                {orders.length} Critical Orders (Page {currentPage})
              </h3>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-3 py-2 text-left w-12">SL</th>
                    <th className="px-3 py-2 text-left">Order</th>
                    <th className="px-3 py-2 text-left">Patient</th>
                    <th className="px-3 py-2 text-left">Type</th>
                    <th className="px-3 py-2 text-left">Booking Day</th>
                    <th className="px-3 py-2 text-left">Slot (IST)</th>
                    <th className="px-3 py-2 text-left">Address</th>
                    <th className="px-3 py-2 text-left">Source</th>
                    <th className="px-3 py-2 text-left">Time Left</th>
                    <th className="px-3 py-2 text-right">Action</th>
                  </tr>
                </thead>

                <tbody className="divide-y">
                  {loading ? (
                    <tr>
                      <td colSpan="10" className="p-6 text-center">
                        Loading...
                      </td>
                    </tr>
                  ) : orders.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="p-6 text-center text-gray-500">
                        No expiring orders 🎉
                      </td>
                    </tr>
                  ) : (
                    orders.map((order, index) => {
                      const minsLeft = getMinsLeftToSlotStart(order);

                      return (
                        <tr key={order.id} className="hover:bg-red-50">
                          <td className="px-3 py-2 font-medium text-center">
                            {getSerialNumber(index)}
                          </td>

                          <td className="px-3 py-2 font-medium">
                            ORD-{order.id}
                            <div className="text-[11px] text-gray-500">
                              #{order.orderNumber}
                            </div>
                          </td>

                          <td className="px-3 py-2">
                            <div className="font-medium">
                              {order.patient?.fullName || "N/A"}
                            </div>
                            <div className="text-gray-500">{order.patient?.contactNo}</div>
                          </td>

                          <td className="px-3 py-2">
                            {order.isHomeSample ? "🏠 Home Sample" : "🏥 Center Visit"}
                          </td>

                          <td className="px-3 py-2">
                            <div className="font-medium">{formatISTDate(order.date)}</div>
                            <div className="text-[11px] text-gray-500">
                              Booked: {formatISTDateTime(order.createdAt)}
                            </div>
                          </td>

                          <td className="px-3 py-2">
                            <div className="font-medium">{order.slot?.name || "N/A"}</div>
                            <div className="text-gray-500">{formatSlotIST(order)}</div>
                          </td>

                          <td className="px-3 py-2 text-gray-700">
                            {formatAddress(order.address)}
                            {!!order?.address?.latitude && !!order?.address?.longitude && (
                              <div className="text-[11px] text-gray-500">
                                Lat: {Number(order.address.latitude).toFixed(5)}, Lng:{" "}
                                {Number(order.address.longitude).toFixed(5)}
                              </div>
                            )}
                          </td>

                          <td className="px-3 py-2">{order.source || "-"}</td>

                          <td className="px-3 py-2 font-semibold">
                            {minsLeft === null ? (
                              <span className="text-gray-500">N/A</span>
                            ) : minsLeft > 0 ? (
                              <span className="text-red-600">{minsLeft} mins</span>
                            ) : (
                              <span className="text-red-700">{getTimeLeftText(order)}</span>
                            )}
                          </td>

                          <td className="px-3 py-2 text-right">
                            <Button
                              size="sm"
                              className="text-xs bg-red-600 hover:bg-red-700 text-white"
                              onClick={() => navigate(`/orders/${order.id}`)}
                            >
                              View Order
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {orders.length > 0 && (
              <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing {getSerialNumber(0)} to {getSerialNumber(orders.length - 1)} of{" "}
                  {totalOrders} orders
                </div>

                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="text-xs"
                  >
                    Previous
                  </Button>

                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) pageNum = i + 1;
                    else if (currentPage <= 3) pageNum = i + 1;
                    else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                    else pageNum = currentPage - 2 + i;

                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? "primary" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(pageNum)}
                        className={`text-xs w-8 h-8 p-0 ${
                          currentPage === pageNum ? "bg-red-600 text-white" : "text-gray-700"
                        }`}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="text-xs"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ExpiringOrders;